/**
 * vendorsApi — minimal client for the M05 vendor master
 * (NestJS `@Controller('v1/master/vendors')`, reached as `/api/v1/master/vendors`
 * through the Vite `/api → /v1/v1` proxy).
 *
 * Only the read side is exposed here: the New User screen needs to attach a
 * VENDOR login, and its field force, to the vendor firm they belong to.
 */
import { apiRequest } from './client.js';

export interface VendorSummary {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly recordStatusCode?: string;
}

interface VendorListWire {
  readonly data?: readonly VendorSummary[];
  readonly total?: number;
}

async function listByStatus(
  status: string,
  token?: string | null,
): Promise<readonly VendorSummary[]> {
  const res = await apiRequest<VendorListWire | readonly VendorSummary[]>(
    `/v1/master/vendors?status=${status}&limit=200`,
    { token },
  );
  if (Array.isArray(res)) return res as readonly VendorSummary[];
  return (res as VendorListWire).data ?? [];
}

/**
 * Vendors a login may be attached to. PENDING_APPROVAL is included because
 * vendor.service.ts creates every vendor in that state, so a firm registered
 * moments ago on /admin/vendors/new would otherwise be missing from the list
 * the operator was sent here to pick from. The backend filters on one status
 * per call, hence the two requests. Either failing fails the list: a list
 * missing its pending half would hide exactly the firm the operator came for.
 */
export async function listVendors(
  token?: string | null,
): Promise<readonly VendorSummary[]> {
  const [active, pending] = await Promise.all([
    listByStatus('ACTIVE', token),
    listByStatus('PENDING_APPROVAL', token),
  ]);
  const seen = new Set(active.map((v) => v.vendorId));
  return [...active, ...pending.filter((v) => !seen.has(v.vendorId))];
}

/**
 * Vendors that may actually be given work.
 *
 * Separate from `listVendors` on purpose: that one deliberately includes
 * PENDING_APPROVAL so the approval dashboard and the login-attachment screen
 * can see a firm that was registered moments ago. Issuing a work order is the
 * opposite case — a vendor the tenant has not approved yet must not be
 * selectable, or the order commits money to a firm that was never cleared.
 */
export function listApprovedVendors(
  token?: string | null,
): Promise<readonly VendorSummary[]> {
  return listByStatus('ACTIVE', token);
}

/**
 * A vendor together with the login issued to it, if any.
 *
 * `loginEmployeeCode` is null for a vendor that has been registered but has no
 * user id yet — the case the Tenant Admin needs to see in order to fix it.
 */
export interface VendorWithLogin {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly contactPerson: string | null;
  readonly mobileNumber: string | null;
  readonly email: string | null;
  readonly recordStatusCode: string;
  readonly loginUserId: string | null;
  readonly loginEmployeeCode: string | null;
  readonly loginStatus: string | null;
}

/** Every vendor of the tenant, any approval status, with its login. */
export function listVendorsWithLogin(
  token?: string | null,
): Promise<readonly VendorWithLogin[]> {
  return apiRequest<readonly VendorWithLogin[]>('/v1/master/vendors/with-login', { token });
}

/**
 * Mirrors CreateVendorDto. A vendor is a billing counterparty, so the
 * statutory, bank and KYC evidence fields are mandatory here exactly as the
 * server requires them — an optional copy of this type is how a second,
 * thinner vendor form gets written by accident.
 */
export interface CreateVendorInput {
  readonly vendorName: string;
  readonly legalName: string;
  readonly contactPerson: string;
  readonly mobileNumber: string;
  readonly email: string;
  readonly address: string;
  readonly gstNumber: string;
  readonly panNumber: string;
  readonly bankAccountNumber: string;
  readonly ifscCode: string;
  readonly gstCertDocRef: string;
  readonly panCardDocRef: string;
  readonly cancelledChequeDocRef: string;
  readonly tanNumber?: string;
  readonly workSpecializations?: readonly string[];
  readonly serviceZones?: readonly string[];
  readonly territoryCoverage?: readonly string[];
}

/**
 * Creates the vendor firm. vendorCode is deliberately omitted so the server
 * allocates the next <TENANT_CODE>_V0001 under an advisory lock.
 */
export async function createVendor(
  input: CreateVendorInput,
  token?: string | null,
): Promise<VendorSummary> {
  return apiRequest<VendorSummary>('/v1/master/vendors', {
    method: 'POST',
    token,
    body: input as unknown as Record<string, unknown>,
  });
}
